import { UTCDate } from '@/utils/dates';
import { Currency, OAuthProvider } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../contexts/data-context/use-data';
import { Button } from '../../../design-system/button/button';
import { ErrorSection } from '../../../design-system/error-section/error-section';
import { Inline } from '../../../design-system/inline/inline';
import { LoadingSection } from '../../../design-system/loading/loading';
import { Stack } from '../../../design-system/stack/stack';
import { AccountView } from '../../../types/entity-types';
import { isBankingAccount } from '../../../utils/accounts/is-banking-account';
import { deleteSwrFetcherWithDynamicUrl } from '../../../utils/delete-swr-fetcher-with-dynamic-url';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';
import type { OAuthConnectionInformation } from '../types/types';
import { AvailableFromXeroTable } from './available-from-xero-table';
import { BankFeedDisconnectConfirmationModal } from './bank-feed-disconnect-confirmation-modal';
import { BankFeedsTable } from './bank-feeds-table';
import type { BankFeedConnection } from './bank-feeds-table-column-definition';
import { BankFeedsTableWrapper } from './bank-feeds-table-wrapper';
import { ConnectedOAuthProviderCard } from './connected-oauth-provider-card';

const API_URL_ACCOUNTING = '/api/accounting/';

/**
 * Separates bank feed connections into Seapoint accounts (connected) and Xero-only accounts.
 * Only connected Seapoint accounts are included; disconnected ones are hidden.
 */
function separateConnections(connections: BankFeedConnection[] | undefined) {
  if (!connections) return { seapointConnections: [], xeroOnlyConnections: [] };

  const seapointConnections: BankFeedConnection[] = [];
  const xeroOnlyConnections: BankFeedConnection[] = [];

  for (const connection of connections) {
    if (connection.isXeroOnly) {
      xeroOnlyConnections.push(connection);
    } else if (connection.isConnected) {
      seapointConnections.push(connection);
    }
  }

  return { seapointConnections, xeroOnlyConnections };
}

/**
 * Enriches Seapoint connections with full account data (institution logos, balances).
 */
function enrichConnectionsWithAccountData(connections: BankFeedConnection[], accounts: AccountView[]) {
  return connections.map((connection) => {
    const account = accounts.find((acc) => acc.id === connection.seapointAccountId);

    return {
      ...connection,
      institutionLogo: account?.institution?.logo || connection.institutionLogo,
      institutionName: account?.institution?.name || connection.institutionName,
      availableBalance: account && isBankingAccount(account) ? account.availableBalance : undefined,
    };
  });
}

type XeroConnectedProviderDetailsProps = {
  connectionStatus: OAuthConnectionInformation;
  onGoBack: () => void;
  onDisconnect: () => void;
  /** When true, shows a warning that data may be outdated due to rate limiting */
  isRateLimited?: boolean;
};

export const XeroConnectedProviderDetails = ({
  connectionStatus,
  onGoBack,
  onDisconnect,
  isRateLimited,
}: XeroConnectedProviderDetailsProps) => {
  const navigate = useNavigate();
  const { isXeroAccountBalanceImportEnabled } = useFlags();

  const addedOnDate = connectionStatus.authorisedAt || connectionStatus.createdAt;
  const connectionDate = addedOnDate ? new UTCDate(addedOnDate) : undefined;

  const [disconnectingFeedId, setDisconnectingFeedId] = useState<string | null>(null);
  const [selectedFeedToDisconnect, setSelectedFeedToDisconnect] = useState<{
    id: string;
    accountName: string;
    currency: string;
  } | null>(null);
  const [importingXeroAccountId, setImportingXeroAccountId] = useState<string | null>(null);

  const isDisconnectModalOpen = selectedFeedToDisconnect !== null;

  const { data: contextData, mutations } = useData();
  const allAccounts = contextData?.accounts || [];

  // Fetch bank feed connections
  const {
    data: bankFeedsData,
    isLoading: isLoadingBankFeeds,
    error: bankFeedsError,
    mutate: mutateBankFeeds,
  } = useSWR<{ connections: BankFeedConnection[] }, Error, string | null>(
    connectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${connectionStatus.connectionId}/bank-feed-connections`
      : null,
  );

  // Disconnect bank feed mutation
  const { trigger: triggerDisconnectBankFeed, isMutating: isDisconnectingFeed } = useSWRMutation<
    { success: boolean; message: string },
    Error,
    string | null,
    { urlSuffix: string }
  >(
    connectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${connectionStatus.connectionId}/bank-feed-connections`
      : null,
    deleteSwrFetcherWithDynamicUrl,
  );

  // Reconnect bank feed mutation
  const { trigger: triggerReconnectBankFeed, isMutating: isReconnectingFeed } = useSWRMutation<
    { success: boolean; message: string; seapointAccountId: string },
    Error,
    string | null,
    { urlSuffix: string }
  >(
    connectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${connectionStatus.connectionId}/bank-feed-connections`
      : null,
    async (url, { arg }: { arg: { urlSuffix: string } }) => {
      const response = await fetch(`${url}${arg.urlSuffix}/reconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to reconnect bank feed');
      return response.json();
    },
  );

  // Import Xero account mutation
  const { trigger: triggerImportXeroAccount, isMutating: isImportingXeroAccount } = useSWRMutation<
    {
      success: boolean;
      message: string;
      account: { id: string; name: string; currency: string; xeroAccountId: string };
    },
    Error,
    string | null,
    { xeroAccountId: string }
  >(
    connectionStatus?.connectionId ? `${API_URL_ACCOUNTING}${connectionStatus.connectionId}/import-xero-account` : null,
    postSwrFetcher,
  );

  // Process connections data
  const { seapointConnections, xeroOnlyConnections } = separateConnections(bankFeedsData?.connections);
  const enrichedConnections = enrichConnectionsWithAccountData(seapointConnections, allAccounts);

  const handleDisconnectFeedClick = (mappingId: string, accountName: string, currency: string) => {
    setSelectedFeedToDisconnect({ id: mappingId, accountName, currency });
  };

  const handleConfirmDisconnect = async () => {
    if (!selectedFeedToDisconnect) {
      Sentry.captureMessage('handleConfirmDisconnect called with null selectedFeedToDisconnect', {
        level: 'warning',
        extra: {
          connectionId: connectionStatus.connectionId,
          message: 'This should not happen - modal was confirmed but no feed was selected',
        },
      });
      return;
    }

    setDisconnectingFeedId(selectedFeedToDisconnect.id);
    try {
      await triggerDisconnectBankFeed({ urlSuffix: `/${selectedFeedToDisconnect.id}` });
      toast.success('Bank feed disconnected successfully');
      await mutateBankFeeds();
      setSelectedFeedToDisconnect(null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          error,
          mappingId: selectedFeedToDisconnect.id,
          connectionId: connectionStatus.connectionId,
          message: 'Failed to disconnect bank feed',
        },
      });
      toast.error('Failed to disconnect bank feed');
    } finally {
      setDisconnectingFeedId(null);
    }
  };

  const handleReconnectFeed = async (mappingId: string) => {
    setDisconnectingFeedId(mappingId);
    try {
      const result = await triggerReconnectBankFeed({ urlSuffix: `/${mappingId}` });
      navigate(`/integrations?provider=xero&success=true&accountId=${result.seapointAccountId}`);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          error,
          mappingId,
          connectionId: connectionStatus.connectionId,
          message: 'Failed to prepare remapping',
        },
      });
      toast.error('Failed to prepare account for remapping');
    } finally {
      setDisconnectingFeedId(null);
    }
  };

  const handleImportXeroAccount = async (xeroAccountId: string) => {
    setImportingXeroAccountId(`xero-${xeroAccountId}`);
    try {
      const result = await triggerImportXeroAccount({ xeroAccountId });
      if (result?.success) {
        toast.success(result.message);
        await Promise.all([mutateBankFeeds(), mutations.mutateAccounts?.()]);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          error,
          xeroAccountId,
          connectionId: connectionStatus.connectionId,
          message: 'Failed to import Xero account',
        },
      });
      toast.error('Failed to import account from Xero');
    } finally {
      setImportingXeroAccountId(null);
    }
  };

  const handleBankFeedDisconnect = (mappingId: string) => {
    const connection = bankFeedsData?.connections.find((c) => c.id === mappingId);
    if (connection) {
      handleDisconnectFeedClick(mappingId, connection.seapointAccountName, connection.currency);
    }
  };

  const handleBankFeedReconnect = (id: string) => {
    const connection = bankFeedsData?.connections.find((c) => c.id === id);
    if (!connection) return;

    if (connection.externalAccountId) {
      // Has a mapping (soft-deleted) - use reconnect flow
      handleReconnectFeed(id);
    } else {
      // Never been mapped - just navigate to mapping modal
      navigate(`/integrations?provider=xero&success=true&accountId=${connection.seapointAccountId}`);
    }
  };

  return (
    <Stack space={10} align="stretch" className="px-6 pt-4">
      <Inline space={4}>
        <Button variant="ghost" size="icon" aria-label="Return to integrations page" onClick={onGoBack}>
          <div className="h-10 w-10 rounded-full bg-muted p-2">
            <ChevronLeft className="h-full w-full" />
          </div>
        </Button>

        <Stack>
          <p className="text-xs leading-5 font-normal tracking-tight text-muted-foreground">Integrations</p>
          <h3 className="text-2xl leading-5 font-semibold tracking-tight text-black">Xero</h3>
        </Stack>
      </Inline>

      <ConnectedOAuthProviderCard
        provider={OAuthProvider.XERO}
        displayName={connectionStatus?.email}
        connectionDate={connectionDate}
        onDisconnect={onDisconnect}
        isRateLimited={isRateLimited}
      />

      {/* Bank Feeds Section */}
      {isLoadingBankFeeds && <LoadingSection text="Loading bank feeds..." />}

      {bankFeedsError && <ErrorSection error={bankFeedsError} />}

      {enrichedConnections.length > 0 && (
        <BankFeedsTableWrapper
          title="Connected accounts"
          description="Manage the mapping of each of your accounts to their own bank accounts in Xero. Seapoint accounts will sync transactions and invoices while non Seapoint accounts will only sync invoices."
        >
          <BankFeedsTable
            connections={enrichedConnections}
            onDisconnect={handleBankFeedDisconnect}
            onReconnect={handleBankFeedReconnect}
            isDisconnecting={(isDisconnectingFeed || isReconnectingFeed) && disconnectingFeedId !== null}
          />
        </BankFeedsTableWrapper>
      )}

      {bankFeedsData && seapointConnections.length === 0 && (
        <BankFeedsTableWrapper
          title="Connected accounts"
          description="No bank accounts are currently connected. Connect your Seapoint accounts to Xero to start syncing transactions."
        >
          <p className="px-5 py-4 text-sm text-muted-foreground">No connected accounts</p>
        </BankFeedsTableWrapper>
      )}

      {/* Available from Xero Section - shows Xero accounts that can be imported */}
      {isXeroAccountBalanceImportEnabled && xeroOnlyConnections.length > 0 && (
        <BankFeedsTableWrapper
          title="Available from Xero"
          description="Import these Xero accounts, not currently in Seapoint, to generate complete reports and track spending across all your accounts."
        >
          <AvailableFromXeroTable
            connections={xeroOnlyConnections}
            onImport={handleImportXeroAccount}
            onDisconnect={handleDisconnectFeedClick}
            isLoading={isImportingXeroAccount}
            loadingId={importingXeroAccountId}
          />
        </BankFeedsTableWrapper>
      )}

      <BankFeedDisconnectConfirmationModal
        isOpen={isDisconnectModalOpen}
        isLoading={isDisconnectingFeed}
        accountName={selectedFeedToDisconnect?.accountName || ''}
        currency={(selectedFeedToDisconnect?.currency as Currency) || Currency.EUR}
        onClose={() => setSelectedFeedToDisconnect(null)}
        onDisconnect={handleConfirmDisconnect}
      />
    </Stack>
  );
};
